import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Brand, Tag, TagType } from "@shared/types";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";

type FormState = {
  name: string;
  website_url: string;
  instagram_handle: string;
  country: string;
  region: string;
  short_summary: string;
  long_summary: string;
  price_level: string;
  gender_focus: string;
  status: string;
  safety_status: string;
};

const EMPTY: FormState = {
  name: "", website_url: "", instagram_handle: "", country: "", region: "",
  short_summary: "", long_summary: "", price_level: "", gender_focus: "",
  status: "candidate", safety_status: "unknown",
};

export function AdminBrandEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === "new";

  const [form, setForm] = useState<FormState>(EMPTY);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    api.tags.list().then((r) => setAllTags(r.tags));
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    api.admin.brandGet(id).then(({ brand: b }) => {
      setBrand(b);
      setForm({
        name: b.name,
        website_url: b.websiteUrl ?? "",
        instagram_handle: b.instagramHandle ?? "",
        country: b.country ?? "",
        region: b.region ?? "",
        short_summary: b.shortSummary ?? "",
        long_summary: b.longSummary ?? "",
        price_level: b.priceLevel ?? "",
        gender_focus: b.genderFocus ?? "",
        status: b.status,
        safety_status: b.safetyStatus,
      });
      setSelectedTagIds(new Set(b.tags.map((t) => t.id)));
    });
  }, [id, isNew]);

  const tagsByType = useMemo(() => {
    const grouped: Record<TagType, Tag[]> = {
      primary_category: [], secondary_category: [], style_direction: [],
      aesthetic: [], material: [], market: [], price_level: [],
    };
    for (const t of allTags) grouped[t.type].push(t);
    return grouped;
  }, [allTags]);

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <div className="container empty-state">Admin access required.</div>;
  }

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setPending(true);
    try {
      const payload = stripEmpty(form);
      let savedId = id;
      if (isNew) {
        const created = await api.admin.brandCreate({
          name: payload.name,
          websiteUrl: payload.website_url,
          country: payload.country,
          region: payload.region,
          shortSummary: payload.short_summary,
          longSummary: payload.long_summary,
          priceLevel: payload.price_level,
          status: payload.status,
        });
        savedId = created.id;
      } else {
        await api.admin.brandUpdate(id!, payload);
      }
      if (savedId) await api.admin.brandSetTags(savedId, [...selectedTagIds]);
      navigate(`/admin/brands/${savedId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setPending(false);
    }
  };

  const toggleTag = (tagId: string) => {
    const next = new Set(selectedTagIds);
    if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
    setSelectedTagIds(next);
  };

  const onDelete = async () => {
    if (!id || isNew) return;
    if (!confirm("Mark this brand as removed?")) return;
    await api.admin.brandDelete(id);
    navigate("/admin/brands");
  };

  return (
    <div className="container" style={{ paddingBlock: "2rem", maxWidth: 920 }}>
      <h2>{isNew ? "Add brand" : `Edit: ${form.name || "—"}`}</h2>

      <form onSubmit={onSave} style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
          <Field label="Name *">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Website URL">
            <input type="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} />
          </Field>
          <Field label="Instagram handle">
            <input value={form.instagram_handle} onChange={(e) => setForm({ ...form, instagram_handle: e.target.value })} placeholder="@brand" />
          </Field>
          <Field label="Country">
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          </Field>
          <Field label="Region">
            <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
          </Field>
          <Field label="Price level">
            <select value={form.price_level} onChange={(e) => setForm({ ...form, price_level: e.target.value })}>
              <option value="">—</option>
              <option value="budget">Budget</option>
              <option value="accessible">Accessible</option>
              <option value="mid">Mid</option>
              <option value="mid-premium">Mid-premium</option>
              <option value="premium">Premium</option>
              <option value="luxury">Luxury</option>
              <option value="unknown">Unknown</option>
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="candidate">candidate</option>
              <option value="review">review</option>
              <option value="published">published</option>
              <option value="rejected">rejected</option>
              <option value="removed">removed</option>
            </select>
          </Field>
          <Field label="Safety">
            <select value={form.safety_status} onChange={(e) => setForm({ ...form, safety_status: e.target.value })}>
              <option value="unknown">unknown</option>
              <option value="clear">clear</option>
              <option value="flagged">flagged</option>
              <option value="blocked">blocked</option>
            </select>
          </Field>
        </div>

        <Field label="Short summary">
          <textarea rows={2} value={form.short_summary} onChange={(e) => setForm({ ...form, short_summary: e.target.value })} />
        </Field>
        <Field label="Long summary">
          <textarea rows={5} value={form.long_summary} onChange={(e) => setForm({ ...form, long_summary: e.target.value })} />
        </Field>

        <fieldset style={{ border: "1px solid var(--border-soft)", borderRadius: "var(--radius)", padding: "1rem" }}>
          <legend style={{ color: "var(--fg-muted)", padding: "0 0.5rem" }}>Tags</legend>
          {(["primary_category", "secondary_category", "style_direction", "aesthetic", "material", "market"] as TagType[]).map((type) => (
            <div key={type} style={{ marginTop: "0.75rem" }}>
              <div style={{ fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-subtle)", marginBottom: "0.4rem" }}>
                {type.replace(/_/g, " ")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                {tagsByType[type].map((t) => {
                  const on = selectedTagIds.has(t.id);
                  return (
                    <button
                      type="button" key={t.id} onClick={() => toggleTag(t.id)}
                      style={{
                        padding: "0.2rem 0.65rem", fontSize: "0.78rem",
                        border: "1px solid " + (on ? "var(--accent)" : "var(--border)"),
                        background: on ? "var(--accent)" : "transparent",
                        color: on ? "var(--accent-ink)" : "var(--fg-muted)",
                        borderRadius: 999,
                      }}
                    >{t.name}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </fieldset>

        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
          <button type="submit" className="primary" disabled={pending}>
            {pending ? "Saving…" : isNew ? "Create brand" : "Save changes"}
          </button>
          {!isNew && <button type="button" onClick={onDelete}>Remove</button>}
          {brand && <a href={`/brands/${brand.slug}`} target="_blank" rel="noreferrer"><button type="button">View public →</button></a>}
        </div>
        {error && <div style={{ color: "var(--danger)" }}>{error}</div>}
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label>
      <div style={{ marginBottom: "0.35rem", color: "var(--fg-muted)", fontSize: "0.8rem" }}>{label}</div>
      {children}
    </label>
  );
}

function stripEmpty<T extends Record<string, string>>(obj: T): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const [k, v] of Object.entries(obj)) out[k] = v === "" ? null : v;
  return out;
}
